import React, { useState, useEffect } from "react";
import SEO from "../seo";
const LaunchYourXpage = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "pre-launch-checklist",
        "publish-store",
        "announce-launch",
        "pro-tips",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Launch Your Xpage Store – xPAGE Help Center"
        description="Learn how to launch your Xpage store, including pre-launch checklist, publishing your store, announcing your launch, and pro tips for success."
        keywords="xPAGE, launch store, pre-launch checklist, publish store, announce launch, pro tips"
        canonical="https://help.xpage.ai/help/landing-page-compliance/Launch%20Your%20Xpage%20Store"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-systemTitle mb-3">
            Launch Your Xpage Store
          </h1>
          <p className="text-sm text-systemLabel mb-6">
            After completing your setup, testing your landing page, and
            resolving any issues, it's time to make your store live and start
            selling with <strong>xPage</strong>.
          </p>
        </header>

        {/* Overview Section */}
        <section id="overview" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Overview
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              Launching your store on Xpage means:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                Activating public access so customers can visit your landing
                page.
              </li>
              <li>Ensuring your subscription plan is active.</li>
              <li>
                Connecting your domain (default xPage subdomain or a custom
                domain).
              </li>
              <li>Enabling payment gateways and shipping methods.</li>
            </ul>
          </div>
        </section>

        {/* Pre-Launch Checklist */}
        <section id="pre-launch-checklist" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Pre-Launch Checklist
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              Before going live, confirm that you've:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>Added and tested all products.</li>
              <li>Configured your policy pages (Privacy, Terms, Refunds).</li>
              <li>Set up shipping zones, rates, and conditions.</li>
              <li>Configured and tested payment methods.</li>
              <li>
                Reviewed your landing page design and ensured mobile
                responsiveness.
              </li>
              <li>Connected your custom domain if applicable.</li>
            </ul>
          </div>
        </section>

        {/* Publish Your Store */}
        <section id="publish-store" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Publish Your Store
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps:
            </h3>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to the <strong>xPage Admin</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Confirm your store is on an active paid plan:{" "}
                  <strong>Settings</strong> → <strong>Billing</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Navigate to <strong>Online Store</strong> →{" "}
                  <strong>Preferences</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Under <strong>Store Status</strong>, toggle{" "}
                  <strong>Enable Storefront</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click <strong>Save</strong>
                </span>
              </li>
            </ol>

            <p className="text-sm text-systemLabel mt-3">
              Your store is now live and accessible to customers via your
              domain.
            </p>
          </div>
        </section>

        {/* Announce Your Launch */}
        <section id="announce-launch" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Announce Your Launch
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              Maximize visibility and traffic by:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                Sharing your store link on social media platforms like Facebook,
                Instagram, and TikTok.
              </li>
              <li>Sending an announcement email to your mailing list.</li>
              <li>
                Running paid campaigns or collaborating with influencers to
                drive traffic.
              </li>
            </ul>
          </div>
        </section>

        {/* Pro Tips */}
        <section id="pro-tips" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Pro Tips
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="bg-system200 border-l-4 border-system400 px-4 py-3">
              <p className="text-sm text-systemLabel mb-2">
                <span className="font-semibold">Pro Tips:</span>
              </p>

              <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
                <li>
                  Monitor store performance using{" "}
                  <strong>Reports & Analytics</strong> to track traffic, sales,
                  and customer behavior.
                </li>
                <li>
                  Continuously optimize product images, descriptions, and
                  checkout flow.
                </li>
                <li>
                  Collect and act on customer feedback promptly to enhance the
                  shopping experience.
                </li>
              </ul>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar - Hidden on mobile, visible on desktop */}
      <div className="hidden lg:block lg:w-64 flex-shrink-0">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#pre-launch-checklist"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "pre-launch-checklist"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Pre-Launch Checklist
              </a>
            </li>
            <li>
              <a
                href="#publish-store"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "publish-store"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Publish Store
              </a>
            </li>
            <li>
              <a
                href="#announce-launch"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "announce-launch"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Announce Launch
              </a>
            </li>
            <li>
              <a
                href="#pro-tips"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "pro-tips"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Pro Tips
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default LaunchYourXpage;
